import React, { useCallback, useState, useEffect } from 'react';
import { Resizable, ResizeCallback, ResizeStartCallback } from 're-resizable';
import { ResizerHandle } from './resizer-handle';
import {
  COPILOT_WIDTH,
  COPILOT_MAX_WIDTH,
  COPILOT_HEIGHT,
  COPILOT_RESIZER_PREFERENCE_KEY,
  getCopilotWidthFromPreference,
  ICopilotResizerPreferenceService,
} from './utils';

export interface ICopilotResizerProps {
  children?: React.ReactNode;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  height?: number | string;
  retainWidthOnRefresh?: boolean;
  onResizeStart?: (width: number) => void;
  onResizeEnd?: (width: number) => void;
  preferenceService?: ICopilotResizerPreferenceService;
  isNavRefreshNew?: boolean;
}

type ResizableHtmlProps = React.ComponentProps<typeof Resizable> & React.HTMLAttributes<HTMLDivElement>;
const ResizableWithHtmlProps = Resizable as unknown as React.ComponentType<ResizableHtmlProps>;

export const CopilotResizer: React.FC<ICopilotResizerProps> = (props) => {
  const {
    children,
    initialWidth = COPILOT_WIDTH,
    minWidth = COPILOT_WIDTH,
    maxWidth = COPILOT_MAX_WIDTH, // Default max width
    height = COPILOT_HEIGHT,
    retainWidthOnRefresh = true,
    preferenceService,
    onResizeStart,
    onResizeEnd,
    isNavRefreshNew,
  } = props;

  const [width, setWidth] = useState(initialWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [computedMaxWidth, setComputedMaxWidth] = useState(Math.min(maxWidth, window.innerWidth * 0.7));

  useEffect(() => {
    const handleResize = () => {
      setComputedMaxWidth(Math.min(maxWidth, window.innerWidth * 0.6));
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [maxWidth]);

  useEffect(() => {
    const newWidth = getCopilotWidthFromPreference(retainWidthOnRefresh, preferenceService);
    if (newWidth !== width) {
      setWidth(newWidth);
    }
  }, [retainWidthOnRefresh]);

  const handleResizeStart = useCallback<ResizeStartCallback>((_e, _dir, _ref) => {
    onResizeStart?.(width);
    setIsDragging(true);
  }, []);

  const handleResizeStop = useCallback<ResizeCallback>((_e, _dir, _ref, d) => {
    setWidth((prevWidth: number) => {
      const newWidth = Math.min(computedMaxWidth, prevWidth + d.width);
      onResizeEnd?.(newWidth);

      if (retainWidthOnRefresh && preferenceService) {
        preferenceService.setAtUserLevel({
          Data: newWidth.toString(),
          Name: COPILOT_RESIZER_PREFERENCE_KEY,
        });
      }

      return newWidth;
    });
    setIsDragging(false);
  }, [computedMaxWidth]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const step = e.key === 'ArrowLeft' ? 10 : -10;
    setWidth((prevWidth) => {
      const newWidth = Math.min(computedMaxWidth, Math.max(minWidth, prevWidth + step));
      onResizeEnd?.(newWidth);
      return newWidth;
    });
  }, [minWidth, computedMaxWidth, onResizeEnd]);

  return (
    <ResizableWithHtmlProps
      data-testid="copilot-resizer"
      size={{ width, height }}
      minWidth={minWidth}
      maxWidth={computedMaxWidth}
      enable={{
        top: false, right: false, bottom: false, left: true,
      }}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      handleComponent={{
        left: <ResizerHandle isDragging={isDragging} isNavRefreshNew={isNavRefreshNew} />,
      }}
      handleStyles={{ left: { left: 0, zIndex: 2 } }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {children}
    </ResizableWithHtmlProps>
  );
};
